/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {AlertModalService} from '../../../../common/component/modal';
import {EnlargementUtilService} from './enlargement-util.service';
import {PlanModelService} from '../../../../common/service/plan-model.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';
import {DataFrameService} from '../../../../common/service/data-frame-service';
import {MapService} from '../../../services/map.service';
import {EnlargementClippingService} from './enlargement-clipping.service';
import {EnlargementDeleteService} from './enlargement-delete.service';
import {EnlargementSelectionService} from './enlargement-selection.service';
import {MapModeService} from '../../../services/map-mode.service';
import {EnlargementCreateService} from './enlargement-create.service';
import {NgEventBus} from 'ng-event-bus';
import {concatMap, finalize, Observable, of, tap} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {forkJoinDefaultIfEmpty} from '../../../../common/observable.utils';

@Injectable({
    providedIn: 'root'
})
export class EnlargementService {
    constructor(
        private alertModalService: AlertModalService,
        private dataFrameService: DataFrameService,
        private mapService: MapService,
        private enlargementClippingService: EnlargementClippingService,
        private enlargementCreateService: EnlargementCreateService,
        private enlargementDeleteService: EnlargementDeleteService,
        private enlargementSelectionService: EnlargementSelectionService,
        private enlargementUtilService: EnlargementUtilService,
        private planModelService: PlanModelService,
        private spatialUtilService: SpatialUtilService,
        private mapModeService: MapModeService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus
    ) {}

    title = 'EnlargementService';

    /**
     * Returns a flag indicating if the enlargement can be saved or not - true if save should be enalbed, false otherwise.
     */
    canSave(attributes: any, enlargementFeature: any) {
        if (
            attributes.scale &&
            attributes.scale.referenceScale &&
            '1:' + enlargementFeature.attributes['scale'] !== attributes.scale.referenceScale.trim()
        ) {
            return true;
        }

        return attributes.fullpage.description !== enlargementFeature.attributes.fullpage;
    }

    /**
     * Save the enlargement.
     */
    save(
        attributes: any,
        graphic: any,
        enlargementFeature: any,
        clippingHoleGeometry: any,
        clippingUnmaskedGeometry: any,
        paperSize: any,
        currentClippingFeatures: any,
        modifiedEnlargement: boolean
    ) {
        console.debug(`${this.title} save`);

        const originalName = enlargementFeature.attributes.name;
        enlargementFeature.attributes.name = attributes.name;

        const updateEnlargementCallback = () => {
            this.eventBus.cast('updateEnlargement', {
                enlargement: enlargementFeature,
                originalName: originalName
            });
        };

        if (this.canSave(attributes, enlargementFeature) || modifiedEnlargement) {
            const refScale = attributes.scale.referenceScale;
            const fourPerPage = attributes.fullpage.description === '4 Per Page';
            const enlargementExtent = enlargementFeature.geometry.getExtent();

            const spatialSettings = this.planModelService.getModel().spatialSettings;
            const coordSystemId = spatialSettings.projectedCoordSystem.id;
            const paperSizeId = parseInt(paperSize.id);
            const scale = parseInt(refScale.split(':')[1] || '1');
            const qtdPerPage = fourPerPage ? 4 : 1;
            const extent = {
                xmin: enlargementExtent.xmin,
                xmax: enlargementExtent.xmax,
                ymin: enlargementExtent.ymin,
                ymax: enlargementExtent.ymax
            };

            const dataFrameExtent = this.dataFrameService.getDataFrame(coordSystemId, paperSizeId, scale, qtdPerPage, extent);

            const polygon = new this.esriModuleService.Polygon(this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference()));
            polygon.addRing([
                [dataFrameExtent.xmin, dataFrameExtent.ymin],
                [dataFrameExtent.xmin, dataFrameExtent.ymax],
                [dataFrameExtent.xmax, dataFrameExtent.ymax],
                [dataFrameExtent.xmax, dataFrameExtent.ymin],
                [dataFrameExtent.xmin, dataFrameExtent.ymin]
            ]);
            // recreate enlargement using the modified details
            return this.enlargementCreateService
                .recreateEnlargements(attributes, polygon, enlargementFeature, attributes.scale, fourPerPage, false)
                .pipe(
                    concatMap(() => {
                        if (clippingHoleGeometry) {
                            return this.enlargementClippingService
                                .createClipping(clippingHoleGeometry, clippingUnmaskedGeometry, enlargementFeature, currentClippingFeatures)
                                .pipe(tap(() => updateEnlargementCallback()));
                        } else {
                            updateEnlargementCallback();
                            return of({});
                        }
                    })
                );
        } else if (clippingHoleGeometry) {
            // create a new clipping
            return this.enlargementClippingService
                .createClipping(clippingHoleGeometry, clippingUnmaskedGeometry, enlargementFeature, currentClippingFeatures)
                .pipe(
                    concatMap(() => this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature])),
                    tap(() => updateEnlargementCallback())
                );
        } else {
            if (currentClippingFeatures && currentClippingFeatures.length > 0) {
                // clipping exists, clear and delete the clipping
                return this.enlargementClippingService.clearAndDeleteClipping(currentClippingFeatures, enlargementFeature).pipe(
                    concatMap(() => this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature])),
                    tap(() => updateEnlargementCallback())
                );
            } else {
                return this.enlargementUtilService.updateEnlargementFeatures([enlargementFeature]).pipe(tap(() => updateEnlargementCallback()));
            }
        }
    }

    /**
     * Updates the given list of enlargement features.
     */
    updateEnlargementFeatures(enlargements: any[]): Observable<void> {
        const graphics = enlargements.map(enlargement => {
            const graphic = new this.esriModuleService.Graphic();
            graphic.setAttributes(enlargement.attributes);
            graphic.setGeometry(enlargement.geometry);
            return graphic;
        });

        return this.spatialUtilService.applyEdits('enlargement', null, graphics, null).pipe(
            map(res => {
                if (res.updates && res.updates.length > 0) {
                    console.debug('Features have been successfully updated!');
                } else {
                    throw new Error('Failed to update features.');
                }
            })
        );
    }

    /**
     * Creates a new enlargement, using the given attributes and graphic.
     */
    create(attributes: any, graphic: any): Observable<void> {
        console.debug(`${this.title} - create`);

        return new Observable(observer => {
            this.enlargementCreateService.createEnlargement(attributes, graphic, (enlargementId: number) => {
                console.debug(`${this.title} - created enlargement: ${enlargementId}`);

                // For geometry features hidden on the main plan, add the enlargement id to their hiddenonenlargements attribute.
                const geometryFeatureLayers = [
                    this.spatialUtilService.getConstructLineMapLayer(),
                    this.spatialUtilService.getConstructPointMapLayer(),
                    this.spatialUtilService.getControlPointMapLayer(),
                    this.spatialUtilService.getLineMapLayer(),
                    this.spatialUtilService.getPointMapLayer(),
                    this.spatialUtilService.getPolygonMapLayer(),
                    this.spatialUtilService.getPolylineMapLayer()
                ];

                const hiddenFeaturesList = geometryFeatureLayers.map(featureLayer =>
                    this.spatialUtilService.getFeatures(featureLayer).filter(f => f.attributes.visible === 'NO')
                );

                hiddenFeaturesList
                    .reduce((acc, features) => {
                        acc.push(...features);
                        return acc;
                    }, [])
                    .forEach(feature => {
                        const hiddenOnEnlargementIds = feature.attributes.hiddenonenlargements
                            ? feature.attributes.hiddenonenlargements
                                  .replace(/[\[\]]/g, '')
                                  .split(',')
                                  .map(s => parseInt(s, 10))
                                  .filter(id => id !== enlargementId)
                            : [];
                        hiddenOnEnlargementIds.push(enlargementId);
                        feature.attributes.hiddenonenlargements = hiddenOnEnlargementIds.map(id => `[${id}]`).join();
                    });

                forkJoinDefaultIfEmpty(
                    geometryFeatureLayers.map((featureLayer, i) => {
                        const features = hiddenFeaturesList[i];
                        if (features.length) {
                            console.debug(`${this.title} - ${featureLayer.id}.applyEdits - num features to update: ${features.length}`);
                            return this.spatialUtilService.applyEdits(featureLayer, null, features, null).pipe(
                                tap(res => {
                                    console.debug(
                                        `${this.title} - ${featureLayer.id}.applyEdits callback - num features updated: ${(res.updates || []).length}`
                                    );
                                })
                            );
                        }
                        return of(null);
                    })
                )
                    .pipe(
                        finalize(() => {
                            observer.next();
                            observer.complete();
                        })
                    )
                    .subscribe();
            });
        });
    }

    /**
     * Deletes the given enlargement feature and clipping feature (optional).
     */
    delete(attributes, enlargementFeature, clippingFeatures) {
        console.debug(this.title + ' delete');

        this.enlargementDeleteService.deleteEnlargement(enlargementFeature).subscribe();
        this.enlargementDeleteService.deleteClippingFeature(clippingFeatures);
    }

    /**
     * Gets the list of enlargements for the current plan.
     */
    /***
     * Return the next page and item order values for new enlargements.
     * @param isFullPage True if the new enlargement is full page.
     * @returns {*} page order, item order
     */
    getNextEnlargementPageItemOrder(isFullPage: boolean) {
        const pageItem = [0, 0];

        const enlargements = this.spatialUtilService.getEnlargements();
        if (enlargements.length === 0) {
            return pageItem;
        }

        const enlargementAttributes = [];
        Array.prototype.push.apply(
            enlargementAttributes,
            enlargements.map(v => {
                return {
                    attributes: window.structuredClone(v.attributes)
                };
            })
        );

        const orderedPageItems = enlargementAttributes.filter(pageItem => {
            return pageItem.attributes.pageorder !== null;
        });

        if (orderedPageItems.length === 0) {
            return pageItem;
        }

        const lastPageOrder = Math.max.apply(
            Math,
            orderedPageItems.map(o => {
                return o.attributes.pageorder;
            })
        );

        if (isFullPage) {
            return [lastPageOrder + 1, 0];
        } else {
            const lastPageItems = enlargementAttributes.filter(pageItem => {
                return pageItem.attributes.pageorder == lastPageOrder;
            });

            let newPageOrder = lastPageOrder;
            let itemOrder = 0;
            if (lastPageItems.length === 0) {
                // blank page, should not happen
            } else if (lastPageItems[0].attributes.fullpage === 'Full Page') {
                newPageOrder = lastPageOrder + 1;
            } else if (lastPageItems.length === 4) {
                newPageOrder = lastPageOrder + 1;
            } else {
                itemOrder = lastPageItems.length;
            }

            return [newPageOrder, itemOrder];
        }
    }

    /**
     * Refreshes all enlargement using with a new paper size.
     */
    refresh(paperSizeId: number) {
        const currentEnlargementId = this.mapModeService.mapMode.id ? this.mapModeService.mapMode.id : null;

        const enlargements = this.spatialUtilService.getEnlargements();
        if (enlargements.length === 0) {
            return of();
        }

        const enlargementTasks = enlargements.map(enlargement => {
            const fourPerPage = enlargement.attributes.fullpage === '4 Per Page';
            const enlargementExtent = enlargement.geometry.getExtent();
            const spatialSettings = this.planModelService.getModel().spatialSettings;
            const coordSystemId = spatialSettings.projectedCoordSystem.id;
            const scale = enlargement.attributes.scale;
            const qtdPerPage = fourPerPage ? 4 : 1;
            const extent = {
                xmin: enlargementExtent.xmin,
                xmax: enlargementExtent.xmax,
                ymin: enlargementExtent.ymin,
                ymax: enlargementExtent.ymax
            };

            const dataFrameExtent = this.dataFrameService.getDataFrame(coordSystemId, paperSizeId, scale, qtdPerPage, extent);

            const polygon = new this.esriModuleService.Polygon(this.esriModuleService.SpatialReference(this.planModelService.getSpatialReference()));
            polygon.addRing([
                [dataFrameExtent.xmin, dataFrameExtent.ymin],
                [dataFrameExtent.xmin, dataFrameExtent.ymax],
                [dataFrameExtent.xmax, dataFrameExtent.ymax],
                [dataFrameExtent.xmax, dataFrameExtent.ymin],
                [dataFrameExtent.xmin, dataFrameExtent.ymin]
            ]);

            return this.enlargementCreateService
                .recreateEnlargements(
                    enlargement.attributes,
                    polygon,
                    enlargement,
                    enlargement.attributes.scale,
                    fourPerPage,
                    this.enlargementUtilService.isMapModeMainPlan() || enlargement.attributes.id !== currentEnlargementId,
                    undefined,
                    true
                )
                .pipe(
                    switchMap(() => {
                        const clippingFeatures = this.spatialUtilService
                            .getClippingGraphics()
                            .filter(f => f.attributes.enlargementid === enlargement.attributes.id);
                        const maskedFeature = clippingFeatures.find(item => item.attributes.type === 1);
                        return this.enlargementClippingService.updateFeatureVisibility(
                            maskedFeature && maskedFeature.geometry,
                            enlargement.attributes.id
                        );
                    })
                );
        });

        return forkJoinDefaultIfEmpty(enlargementTasks).pipe(
            tap(() => {
                enlargements.forEach(enlargement => {
                    this.eventBus.cast('updateEnlargement', {enlargement: enlargement, originalName: enlargement.attributes.name});
                });
            })
        );
    }
}
